import {
  atualizarContaPessoaFisicaSchema,
  atualizarContaPessoaJuridicaSchema,
  cnpjSchema,
  cpfSchema,
  criarContaPessoaFisicaSchema,
  criarContaPessoaJuridicaSchema,
  dataDeNascimentoSchema,
  DIA_MAXIMO_DE_FECHAMENTO,
  emailSchema,
  inscricaoEstadualSchema,
  nomeSchema,
  REGIMES_DE_FATURAMENTO,
  type RegimeDeFaturamento,
  STATUS_DE_CONTA,
  type StatusDaConta,
  telefoneSchema,
  type TipoDeConta,
} from '@locaveri/shared'
import { Building2, LoaderCircle, User } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { deCampoDeReaisParaWire, paraCampoDeReais } from '@/lib/dinheiro'
import {
  aplicarErrosDaApi,
  aplicarErrosDoZod,
  validarCom,
  validarComSeInformado,
} from '@/lib/formulario'
import {
  apenasAlfanumericos,
  apenasDigitos,
  mascararCnpjProgressivo,
  mascararCpfProgressivo,
  mascararTelefoneProgressivo,
} from '@/lib/mascaras'
import { cn } from '@/lib/utils'

import { type Conta, useAtualizarConta, useConta, useCriarConta } from './api'

/**
 * Cadastro e edição de conta de cliente.
 *
 * ---------------------------------------------------------------------------
 * PF E PJ NÃO SÃO O MESMO FORMULÁRIO COM CAMPOS A MAIS
 * ---------------------------------------------------------------------------
 * O contrato é uma união DISCRIMINADA por `tipo` (`@locaveri/shared`), e a
 * validação usa o schema do tipo escolhido. É por isso que o erro sai como
 * "CNPJ é obrigatório" em vez do inútil "nenhuma variante da união casou".
 *
 * O TIPO NÃO É EDITÁVEL depois de criado: trocar PF por PJ trocaria o titular
 * do contrato, das faturas e das multas já emitidas. Isso é conta nova, não
 * edição — e o schema de atualização nem aceita o campo.
 *
 * A validação de CPF/CNPJ é a do `@locaveri/shared`, com dígito verificador de
 * verdade (e CNPJ alfanumérico da IN RFB 2.229/2024). A máscara daqui é só
 * apresentação: o que viaja é sempre a forma canônica, sem pontuação.
 */

const CAMPOS = [
  'cpf',
  'nome',
  'dataDeNascimento',
  'cnpj',
  'razaoSocial',
  'nomeFantasia',
  'inscricaoEstadual',
  'centroDeCusto',
  'emailDeCobranca',
  'telefone',
  'regimeDeFaturamento',
  'diaDeFechamento',
  'limiteDeCredito',
  'observacoes',
  'status',
] as const

const ROTULO_DE_REGIME: Readonly<Record<RegimeDeFaturamento, string>> = {
  PRE_PAGO: 'Pré-pago (cartão na reserva)',
  FATURADO_MENSAL: 'Faturado mensalmente',
}

const ROTULO_DE_STATUS: Readonly<Record<StatusDaConta, string>> = {
  ATIVA: 'Ativa',
  PENDENTE: 'Pendente de análise',
  SUSPENSA: 'Suspensa',
  ENCERRADA: 'Encerrada',
}

interface FormularioDeConta {
  tipo: TipoDeConta
  cpf: string
  nome: string
  dataDeNascimento: string
  cnpj: string
  razaoSocial: string
  nomeFantasia: string
  inscricaoEstadual: string
  centroDeCusto: string
  emailDeCobranca: string
  telefone: string
  regimeDeFaturamento: RegimeDeFaturamento
  diaDeFechamento: string
  limiteDeCredito: string
  observacoes: string
  status: StatusDaConta
}

function valoresIniciais(conta: Conta | undefined): FormularioDeConta {
  return {
    tipo: conta?.tipo ?? 'PF',
    cpf: mascararCpfProgressivo(conta?.cpf ?? ''),
    nome: conta?.nome ?? '',
    dataDeNascimento: (conta?.dataDeNascimento ?? '').slice(0, 10),
    cnpj: mascararCnpjProgressivo(conta?.cnpj ?? ''),
    razaoSocial: conta?.razaoSocial ?? '',
    nomeFantasia: conta?.nomeFantasia ?? '',
    inscricaoEstadual: conta?.inscricaoEstadual ?? '',
    centroDeCusto: conta?.centroDeCusto ?? '',
    emailDeCobranca: conta?.emailDeCobranca ?? '',
    telefone: mascararTelefoneProgressivo(conta?.telefone ?? ''),
    regimeDeFaturamento: conta?.regimeDeFaturamento ?? 'PRE_PAGO',
    diaDeFechamento: conta?.diaDeFechamento === null ? '' : String(conta?.diaDeFechamento ?? ''),
    limiteDeCredito: paraCampoDeReais(conta?.limiteDeCredito),
    observacoes: conta?.observacoes ?? '',
    status: conta?.status ?? 'PENDENTE',
  }
}

export interface DialogoDeContaProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `undefined` cria; preenchido edita. */
  readonly conta?: Conta | undefined
}

export function DialogoDeConta({ aberto, aoFechar, conta }: DialogoDeContaProps): ReactElement {
  const editando = conta !== undefined
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criar = useCriarConta()
  const atualizar = useAtualizarConta()

  /**
   * ⚠️ O DOCUMENTO VEM DO DETALHE, NUNCA DA LINHA DA LISTA.
   *
   * A listagem devolve o CPF MASCARADO (`***.982.247-**`) — é o Adendo E
   * aplicado no servidor. Preencher este formulário com a linha da lista faria
   * o "Salvar" gravar a máscara como documento, e o defeito passaria quieto:
   * a tela mostraria asteriscos e ninguém leria aquilo como erro.
   *
   * O detalhe traz o valor cheio, e a busca dele grava
   * `DADO_DE_CLIENTE_REVELADO` na trilha — que é exatamente a "ação explícita e
   * registrada" que o Adendo E pede. Abrir a edição É a revelação.
   */
  const detalhe = useConta(aberto && conta !== undefined ? conta.id : null)
  const contaCompleta = detalhe.data ?? conta
  const carregandoDocumento = editando && detalhe.isPending

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeConta>({ defaultValues: valoresIniciais(undefined) })

  useEffect(() => {
    if (!aberto) return
    setErroGeral(null)
    // ⚠️ Depende de `contaCompleta`, não de `conta`: quando o detalhe chega, o
    // formulário é repreenchido com o documento em claro. Sem esta dependência
    // o campo ficaria com a máscara que veio da lista.
    if (editando && detalhe.isPending) return
    reset(valoresIniciais(contaCompleta))
  }, [aberto, contaCompleta, editando, detalhe.isPending, reset])

  const tipo = watch('tipo')
  const regime = watch('regimeDeFaturamento')
  const cpf = watch('cpf')
  const cnpj = watch('cnpj')
  const telefone = watch('telefone')
  const faturado = regime === 'FATURADO_MENSAL'

  // ⚠️ `carregandoDocumento` entra aqui, e é o que impede a janela entre abrir
  // a edição e o detalhe chegar: nesse intervalo o formulário ainda tem a
  // MÁSCARA que veio da lista, e um "Salvar" apressado gravaria asteriscos.
  const ocupado = criar.isPending || atualizar.isPending || carregandoDocumento

  function camposComuns(valores: FormularioDeConta): Record<string, unknown> {
    const telefoneLimpo = apenasDigitos(valores.telefone)
    const limiteWire = faturado ? deCampoDeReaisParaWire(valores.limiteDeCredito) : null

    return {
      emailDeCobranca: valores.emailDeCobranca,
      regimeDeFaturamento: valores.regimeDeFaturamento,
      ...(telefoneLimpo.length > 0 ? { telefone: telefoneLimpo } : {}),
      // Dia de fechamento e limite de crédito só fazem sentido no faturado —
      // mandar no pré-pago criaria um dado que ninguém honra.
      ...(faturado && valores.diaDeFechamento.length > 0
        ? { diaDeFechamento: valores.diaDeFechamento }
        : {}),
      ...(limiteWire === null ? {} : { limiteDeCredito: limiteWire }),
      ...(valores.observacoes.trim().length > 0 ? { observacoes: valores.observacoes } : {}),
    }
  }

  function camposDeIdentificacao(valores: FormularioDeConta): Record<string, unknown> {
    if (valores.tipo === 'PF') {
      return {
        cpf: apenasDigitos(valores.cpf),
        nome: valores.nome,
        dataDeNascimento: valores.dataDeNascimento,
      }
    }

    return {
      cnpj: apenasAlfanumericos(valores.cnpj),
      razaoSocial: valores.razaoSocial,
      ...(valores.nomeFantasia.trim().length > 0 ? { nomeFantasia: valores.nomeFantasia } : {}),
      ...(valores.inscricaoEstadual.trim().length > 0
        ? { inscricaoEstadual: valores.inscricaoEstadual }
        : {}),
      ...(valores.centroDeCusto.trim().length > 0 ? { centroDeCusto: valores.centroDeCusto } : {}),
    }
  }

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (conta !== undefined) {
      // Na edição o documento não vai junto: corrigir CPF/CNPJ é operação de
      // exceção, com auditoria própria, e o schema de atualização o omite.
      const { cpf: _cpf, cnpj: _cnpj, ...identificacao } = camposDeIdentificacao(valores)
      const entrada = {
        ...identificacao,
        ...camposComuns(valores),
        status: valores.status,
      }

      const schema =
        conta.tipo === 'PF' ? atualizarContaPessoaFisicaSchema : atualizarContaPessoaJuridicaSchema
      const validado = schema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      atualizar.mutate(
        { id: conta.id, dados: entrada },
        {
          onSuccess: () => {
            toast.success('Conta atualizada')
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    const entrada = {
      tipo: valores.tipo,
      ...camposDeIdentificacao(valores),
      ...camposComuns(valores),
    }

    const schema =
      valores.tipo === 'PF' ? criarContaPessoaFisicaSchema : criarContaPessoaJuridicaSchema
    const validado = schema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    criar.mutate(entrada, {
      onSuccess: () => {
        toast.success('Conta criada', {
          description:
            valores.tipo === 'PJ'
              ? 'Agora vincule os condutores autorizados desta empresa.'
              : 'A conta nasce pendente até a análise de cadastro.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar conta' : 'Nova conta de cliente'}</DialogTitle>
          <DialogDescription>
            A conta é quem paga e quem assina o contrato. Os condutores autorizados são cadastrados
            separadamente.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <fieldset disabled={ocupado || editando} className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Tipo de conta</legend>
            <div role="radiogroup" aria-label="Tipo de conta" className="flex gap-2">
              {(['PF', 'PJ'] as const).map((opcao) => {
                const selecionado = tipo === opcao
                const Icone = opcao === 'PF' ? User : Building2

                return (
                  <button
                    key={opcao}
                    type="button"
                    role="radio"
                    aria-checked={selecionado}
                    disabled={ocupado || editando}
                    onClick={() => {
                      setValue('tipo', opcao)
                    }}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2',
                      'text-sm transition-colors focus-visible:outline-none focus-visible:ring-2',
                      'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70',
                      selecionado
                        ? 'border-primary bg-accent font-medium text-accent-foreground'
                        : 'border-border bg-surface text-fg-secondary hover:bg-elevated',
                    )}
                  >
                    <Icone className="size-4" aria-hidden="true" />
                    {opcao === 'PF' ? 'Pessoa física' : 'Pessoa jurídica'}
                  </button>
                )
              })}
            </div>
            {editando ? (
              <p className="text-xs text-fg-muted">
                O tipo não muda depois de criado — trocar PF por PJ trocaria o titular do contrato.
              </p>
            ) : null}
          </fieldset>

          {tipo === 'PF' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="CPF" obrigatorio erro={errors.cpf?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('cpf', {
                      validate: (valor, valores) =>
                        valores.tipo !== 'PF' || validarCom(cpfSchema)(valor),
                    })}
                    value={cpf}
                    onChange={(evento) => {
                      // `shouldValidate` só depois do primeiro envio: validar a
                      // cada tecla acusaria "CPF inválido" no terceiro dígito.
                      setValue('cpf', mascararCpfProgressivo(evento.target.value), {
                        shouldValidate: isSubmitted,
                      })
                    }}
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    disabled={ocupado || editando}
                    readOnly={editando}
                  />
                )}
              </Campo>

              <Campo rotulo="Nome civil" obrigatorio erro={errors.nome?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('nome', {
                      validate: (valor, valores) =>
                        valores.tipo !== 'PF' || validarCom(nomeSchema)(valor),
                    })}
                    autoComplete="name"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Data de nascimento"
                obrigatorio
                erro={errors.dataDeNascimento?.message}
                dica="O titular precisa ter 18 anos completos."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('dataDeNascimento', {
                      validate: (valor, valores) =>
                        valores.tipo !== 'PF' || validarCom(dataDeNascimentoSchema)(valor),
                    })}
                    type="date"
                    disabled={ocupado}
                  />
                )}
              </Campo>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="CNPJ"
                obrigatorio
                erro={errors.cnpj?.message}
                dica="Aceita o formato alfanumérico (IN RFB 2.229/2024)."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('cnpj', {
                      validate: (valor, valores) =>
                        valores.tipo !== 'PJ' || validarCom(cnpjSchema)(valor),
                    })}
                    value={cnpj}
                    onChange={(evento) => {
                      setValue('cnpj', mascararCnpjProgressivo(evento.target.value), {
                        shouldValidate: isSubmitted,
                      })
                    }}
                    placeholder="00.000.000/0000-00"
                    disabled={ocupado || editando}
                    readOnly={editando}
                  />
                )}
              </Campo>

              <Campo rotulo="Razão social" obrigatorio erro={errors.razaoSocial?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('razaoSocial', {
                      validate: (valor, valores) =>
                        valores.tipo !== 'PJ' || validarCom(nomeSchema)(valor),
                    })}
                    autoComplete="organization"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Nome fantasia" erro={errors.nomeFantasia?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('nomeFantasia', { validate: validarComSeInformado(nomeSchema) })}
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo rotulo="Inscrição estadual" erro={errors.inscricaoEstadual?.message}>
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('inscricaoEstadual', {
                      validate: validarComSeInformado(inscricaoEstadualSchema),
                    })}
                    placeholder="ISENTO"
                    disabled={ocupado}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Centro de custo"
                erro={errors.centroDeCusto?.message}
                dica="Padrão da empresa. Cada condutor pode ter o seu."
                className="sm:col-span-2"
              >
                {(campo) => <Input {...campo} {...register('centroDeCusto')} disabled={ocupado} />}
              </Campo>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="E-mail de cobrança"
              obrigatorio
              erro={errors.emailDeCobranca?.message}
              dica="Pode ser diferente do e-mail de login."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('emailDeCobranca', {
                    required: 'informe o e-mail de cobrança',
                    validate: validarCom(emailSchema),
                  })}
                  type="email"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Telefone" erro={errors.telefone?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('telefone', { validate: validarComSeInformado(telefoneSchema) })}
                  value={telefone}
                  onChange={(evento) => {
                    setValue('telefone', mascararTelefoneProgressivo(evento.target.value), {
                      shouldValidate: isSubmitted,
                    })
                  }}
                  inputMode="tel"
                  placeholder="(11) 98888-7777"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Regime de faturamento" erro={errors.regimeDeFaturamento?.message}>
              {(campo) => (
                <Select {...campo} {...register('regimeDeFaturamento')} disabled={ocupado}>
                  {REGIMES_DE_FATURAMENTO.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DE_REGIME[item]}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            {editando ? (
              <Campo rotulo="Situação" erro={errors.status?.message}>
                {(campo) => (
                  <Select {...campo} {...register('status')} disabled={ocupado}>
                    {STATUS_DE_CONTA.map((item) => (
                      <option key={item} value={item}>
                        {ROTULO_DE_STATUS[item]}
                      </option>
                    ))}
                  </Select>
                )}
              </Campo>
            ) : null}

            {faturado ? (
              <>
                <Campo
                  rotulo="Dia de fechamento"
                  erro={errors.diaDeFechamento?.message}
                  dica={`De 1 a ${String(DIA_MAXIMO_DE_FECHAMENTO)} — 29, 30 e 31 não existem em todo mês.`}
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('diaDeFechamento')}
                      type="number"
                      min={1}
                      max={DIA_MAXIMO_DE_FECHAMENTO}
                      inputMode="numeric"
                      disabled={ocupado}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Limite de crédito"
                  erro={errors.limiteDeCredito?.message}
                  dica="Em reais. Ex.: 15.000,00"
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('limiteDeCredito', {
                        validate: (valor: string) =>
                          valor.trim().length === 0 ||
                          deCampoDeReaisParaWire(valor) !== null ||
                          'valor inválido — use o formato 15.000,00',
                      })}
                      inputMode="decimal"
                      placeholder="0,00"
                      className="tabular-money"
                      disabled={ocupado}
                    />
                  )}
                </Campo>
              </>
            ) : null}
          </div>

          <Campo rotulo="Observações" erro={errors.observacoes?.message}>
            {(campo) => (
              <Textarea
                {...campo}
                {...register('observacoes')}
                rows={3}
                maxLength={2000}
                disabled={ocupado}
              />
            )}
          </Campo>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Criar conta'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
